import { ICellRendererParams } from '@trackman/baseball-shared-components';
import { DataKey } from 'enums/DataKey';
import { ChallengeCall, ChallengeResult, ChallengeTeam, IChallengeTag } from 'interfaces/tagging/ApiModels/IChallengeTag';
import { ReviewDataRowProps } from 'models/review/ReviewDataRow/ReviewDataRowProps';
import React, { useState } from 'react';
import styled from 'styled-components';
import { darkgrey } from 'styles/Colors';
import { ABSModal } from './components/ABSModal';
import { ReactComponent as ABSChallengeIcon } from './icons/ABSChallengeIcon.svg';

const CellTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 13px;
  color: ${darkgrey};
`;

const ResultLabel = styled.span`
  text-decoration: underline;
  color: ${darkgrey};
`;

interface ABSChallengeRendererProps {
  data: ICellRendererParams<string | number, ReviewDataRowProps>;
}

const ABSChallengeRenderer: React.FC<ABSChallengeRendererProps> = (props) => {
  const { data } = props;

  const [open, setOpen] = useState(false);
  const [displayTag, setDisplayTag] = useState<IChallengeTag | null>(data.row.challengeTag ?? null);

  const [challengingTeam, setChallengingTeam] = useState<ChallengeTeam | null>(null);
  const [umpireCall, setUmpireCall] = useState<ChallengeCall | null>(null);
  const [challengeOutcome, setChallengeOutcome] = useState<ChallengeResult | null>(null);

  const handleOpen = () => {
    setChallengingTeam(data.row.challengeTag?.challengeTeam ?? null);
    setUmpireCall(data.row.challengeTag?.challengeCall ?? null);
    setChallengeOutcome(data.row.challengeTag?.challengeResult ?? null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleApply = () => {
    if (!challengingTeam || !umpireCall || !challengeOutcome) return;

    const newTag: IChallengeTag = {
      challengedUtc: data.row.challengeTag?.challengedUtc ?? new Date().toISOString(),
      challengedLocal: data.row.challengeTag?.challengedLocal ?? '',
      challengeType: data.row.challengeTag?.challengeType ?? 'PitchCall',
      challengeTeam: challengingTeam,
      challengeCall: umpireCall,
      challengeResult: challengeOutcome,
    };

    data.row.updateData(newTag as any, DataKey.AbsChallenge, data.row.id);
    setDisplayTag(newTag);
    setOpen(false);
  };

  const handleRemove = () => {
    data.row.updateData(null as any, DataKey.AbsChallenge, data.row.id);
    setDisplayTag(null);
    setOpen(false);
  };

  if (!displayTag) {
    return <div>-</div>;
  }

  return (
    <div>
      <CellTrigger onClick={handleOpen} data-testid='abs-challenge-cell-trigger'>
        <ResultLabel>{displayTag?.challengeResult}</ResultLabel>
        <ABSChallengeIcon />
      </CellTrigger>
      <ABSModal
        open={open}
        isChallenge={!!displayTag}
        onClose={handleClose}
        pitchNumber={data.row.playSequenceNumber}
        inning={`${data.row.inning.isTopInning ? 'Top' : 'Bottom'} of ${data.row.inning.inningNumber}`}
        homeTeamName={data.row.homeTeamName || ''}
        awayTeamName={data.row.awayTeamName || ''}
        challengingTeam={challengingTeam}
        umpireCall={umpireCall}
        challengeOutcome={challengeOutcome}
        onChallengeTeamChange={setChallengingTeam}
        onUmpireCallChange={setUmpireCall}
        onOutcomeChange={setChallengeOutcome}
        onRemoveFromPlay={handleRemove}
        onApplyToPlay={handleApply}
      />
    </div>
  );
};

export const absChallengeRenderer = (data: ICellRendererParams<string | number, ReviewDataRowProps>) => {
  return <ABSChallengeRenderer data={data} />;
};
